import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Avatar,
  TablePagination,
} from '@mui/material';
import {
  QrCode2 as QRIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Image as ImageIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';

interface QRConfig {
  id: number;
  qr_code_data: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface RechargeRequest {
  id: number;
  user_id: number;
  user_email: string;
  user_name: string;
  amount: number;
  transaction_id: string;
  receipt_image_url: string;
  payment_method: string;
  payment_date: string;
  status: string;
  created_at: string;
  approved_at?: string;
  rejection_reason?: string;
}

const AdminRechargeManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tabValue, setTabValue] = useState(0);
  const [qrConfig, setQrConfig] = useState<QRConfig | null>(null);
  const [rechargeRequests, setRechargeRequests] = useState<RechargeRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRequests, setTotalRequests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RechargeRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [qrFileName, setQrFileName] = useState('');
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);

  const [qrFormData, setQrFormData] = useState({
    qr_code_data: '',
    description: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const isAdmin = user.role && [
      'SUPER_ADMIN',
      'CONTENT_MODERATOR',
      'FINANCE_ADMIN',
      'SUPPORT_ADMIN',
      'ID_VERIFICATION_ADMIN',
    ].includes(user.role);

    if (!isAdmin) {
      navigate('/my-profile');
      return;
    }

    fetchData(page, rowsPerPage);
  }, [user, navigate, statusFilter, page, rowsPerPage]);

  const fetchData = async (currentPage = 0, currentRowsPerPage = 10) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('skip', String(currentPage * currentRowsPerPage));
      params.append('limit', String(currentRowsPerPage));
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const [qrResponse, requestsResponse] = await Promise.all([
        api.get('/admin/payment-gateway/qr-code'),
        api.get(`/admin/payment-gateway/recharge-requests?${params.toString()}`),
      ]);

      setQrConfig(qrResponse.data.data || null);
      setRechargeRequests(requestsResponse.data.data || []);
      setTotalRequests(requestsResponse.data.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleQrFormChange = (e: any) => {
    const { name, value } = e.target;
    setQrFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQrFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setQrFormData((prev) => ({ ...prev, qr_code_data: result }));
      setQrFileName(file.name);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveQrCode = async () => {
    if (!qrFormData.qr_code_data) {
      toast.error('Please enter QR code data');
      return;
    }

    try {
      if (qrConfig) {
        await api.put(`/admin/payment-gateway/qr-code/${qrConfig.id}`, qrFormData);
        toast.success('QR code updated successfully');
      } else {
        await api.post('/admin/payment-gateway/qr-code', qrFormData);
        toast.success('QR code created successfully');
      }
      setQrDialogOpen(false);
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save QR code');
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    try {
      await api.post(`/admin/payment-gateway/recharge-requests/${selectedRequest.id}/approve`, {
        admin_notes: actionNotes,
      });
      toast.success('Recharge approved and account credited!');
      setActionDialogOpen(false);
      setActionNotes('');
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest || !actionNotes) {
      toast.error('Please enter rejection reason');
      return;
    }

    try {
      await api.post(`/admin/payment-gateway/recharge-requests/${selectedRequest.id}/reject`, {
        rejection_reason: actionNotes,
      });
      toast.success('Recharge request rejected');
      setActionDialogOpen(false);
      setActionNotes('');
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  const openReceiptPreview = () => {
    if (!selectedRequest?.receipt_image_url) {
      toast.error('Receipt image is not available');
      return;
    }
    setReceiptPreviewOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const openActionDialog = (request: RechargeRequest, type: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(type);
    setActionNotes('');
    setActionDialogOpen(true);
  };

  const openQrDialog = () => {
    if (qrConfig) {
      setQrFormData({
        qr_code_data: qrConfig.qr_code_data,
        description: qrConfig.description,
      });
      setQrFileName('');
    } else {
      setQrFormData({ qr_code_data: '', description: '' });
      setQrFileName('');
    }
    setQrDialogOpen(true);
  };

  const handleStatusFilterChange = (status: string | null) => {
    setPage(0);
    setStatusFilter(status);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <AdminLayout currentPage="recharge">
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="recharge">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          💳 Recharge Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage QR codes and approve user recharge requests
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label="QR Code Config" icon={<QRIcon />} iconPosition="start" />
          <Tab label="Recharge Requests" />
        </Tabs>
      </Box>

      {/* QR Code Tab */}
      {tabValue === 0 && (
        <Box>
          {qrConfig ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Current QR Code
                      </Typography>
                      <Chip
                        label={qrConfig.is_active ? 'Active' : 'Inactive'}
                        color={qrConfig.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <img
                        src={qrConfig.qr_code_data}
                        alt="QR Code"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          border: '2px solid #E63946',
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {qrConfig.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Created: {new Date(qrConfig.created_at).toLocaleDateString()}
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EditIcon />}
                      sx={{ mt: 2 }}
                      onClick={openQrDialog}
                    >
                      Update QR Code
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      📋 Instructions
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          1. Create/Update QR Code
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Click "Update QR Code" to replace with new payment details.
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          2. Users Scan & Pay
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Users scan the QR code to make payments via UPI or Bank Transfer.
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          3. Verify & Approve
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Go to "Recharge Requests" tab to review and approve payments.
                        </Typography>
                      </Box>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Use a data URI (base64) or URL for the QR code image.
                      </Alert>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Card>
              <CardContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No QR code configured yet.
                </Alert>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#E63946' }}
                  onClick={openQrDialog}
                >
                  Create QR Code
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Recharge Requests Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Filter Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant={statusFilter === null ? 'contained' : 'outlined'}
              onClick={() => handleStatusFilterChange(null)}
              size="small"
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'PENDING' ? 'contained' : 'outlined'}
              onClick={() => handleStatusFilterChange('PENDING')}
              size="small"
              sx={{ backgroundColor: statusFilter === 'PENDING' ? '#ff9800' : undefined }}
            >
              Pending
            </Button>
            <Button
              variant={statusFilter === 'APPROVED' ? 'contained' : 'outlined'}
              onClick={() => handleStatusFilterChange('APPROVED')}
              size="small"
              sx={{ backgroundColor: statusFilter === 'APPROVED' ? '#4caf50' : undefined }}
            >
              Approved
            </Button>
            <Button
              variant={statusFilter === 'REJECTED' ? 'contained' : 'outlined'}
              onClick={() => handleStatusFilterChange('REJECTED')}
              size="small"
              sx={{ backgroundColor: statusFilter === 'REJECTED' ? '#f44336' : undefined }}
            >
              Rejected
            </Button>
          </Box>

          {rechargeRequests.length === 0 ? (
            <Card>
              <CardContent>
                <Alert severity="info">
                  No recharge requests found.
                </Alert>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>User</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell><strong>Transaction ID</strong></TableCell>
                      <TableCell><strong>Method</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rechargeRequests.map((request) => (
                      <TableRow key={request.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {request.user_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {request.user_email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2A9D8F' }}>
                            {request.amount.toFixed(2)} Points
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {request.transaction_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={request.payment_method} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.status.toUpperCase()}
                            color={getStatusColor(request.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(request.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="center">
                          {request.status.toUpperCase() === 'PENDING' ? (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                onClick={() => openActionDialog(request, 'approve')}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<RejectIcon />}
                                onClick={() => openActionDialog(request, 'reject')}
                              >
                                Reject
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedRequest(request);
                                setActionDialogOpen(true);
                              }}
                            >
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRequests}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Card>
          )}
        </Box>
      )}

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {qrConfig ? 'Update QR Code' : 'Create QR Code'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Alert severity="info">
            Upload a QR code image (PNG, JPG, or WEBP).
          </Alert>

            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Upload QR Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleQrFileUpload}
              />
            </Button>

            {qrFileName && (
              <Typography variant="caption" color="textSecondary">
                Selected file: {qrFileName}
              </Typography>
            )}

            {!qrFileName && qrFormData.qr_code_data && (
              <Typography variant="caption" color="textSecondary">
                Current QR image loaded. Upload a new file only if you want to replace it.
              </Typography>
            )}

          <TextField
            label="Description"
            name="description"
            value={qrFormData.description}
            onChange={handleQrFormChange}
            fullWidth
            size="small"
            placeholder="e.g., UPI: user@bank, Account: XXXX"
          />

          {qrFormData.qr_code_data && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                Preview:
              </Typography>
              <img
                src={qrFormData.qr_code_data}
                alt="QR Preview"
                style={{ maxWidth: '200px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveQrCode}
            variant="contained"
            sx={{ backgroundColor: '#E63946' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog (Approve/Reject) */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Recharge' : 'Reject Recharge'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {selectedRequest && (
            <>
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="body2"><strong>User:</strong> {selectedRequest.user_name}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {selectedRequest.user_email}</Typography>
                <Typography variant="body2"><strong>Amount:</strong> {selectedRequest.amount.toFixed(2)} Points</Typography>
                <Typography variant="body2"><strong>Transaction ID:</strong> {selectedRequest.transaction_id}</Typography>
              </Box>

              {selectedRequest.receipt_image_url && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    Receipt:
                  </Typography>
                  <Button size="small" startIcon={<ImageIcon />} onClick={openReceiptPreview}>
                    View Receipt
                  </Button>
                </Box>
              )}

              <TextField
                label={actionType === 'approve' ? 'Notes (optional)' : 'Rejection Reason'}
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                size="small"
                required={actionType === 'reject'}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={actionType === 'approve' ? handleApproveRequest : handleRejectRequest}
            variant="contained"
            sx={{ backgroundColor: actionType === 'approve' ? '#4caf50' : '#f44336' }}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={receiptPreviewOpen}
        onClose={() => setReceiptPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Receipt Preview</DialogTitle>
        <DialogContent>
          {selectedRequest?.receipt_image_url ? (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <img
                src={selectedRequest.receipt_image_url}
                alt="Payment receipt"
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', border: '1px solid #ddd' }}
              />
            </Box>
          ) : (
            <Alert severity="warning">Receipt image not found.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminRechargeManagementPage;
